import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  HeartHandshake,
  BookOpen,
  Accessibility,
  Megaphone,
  MapPin,
  Phone,
  Mail,
  ArrowRight,
  Quote,
  Heart,
  Compass,
  Users,
  HandHeart,
  Gift,
  CreditCard,
  UserPlus,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import ContactForm from "@/components/ContactForm";
import Seo from "@/components/Seo";
import {
  ORG,
  FACTS,
  WHAT_WE_DO_INTRO,
  BENEFITS,
  HELP_STEPS,
  TESTIMONIALS,
  TEAM,
  BLOG_LIST,
  CONTACT,
} from "@/data/content";

const ICONS = { HeartHandshake, Users, BookOpen, Megaphone, Accessibility, HandHeart };

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut", delay: i * 0.07 },
  }),
};

const Overline = ({ children }) => (
  <span className="text-xs font-bold tracking-[0.2em] uppercase text-orange-700/80">{children}</span>
);

const Reveal = ({ children, i = 0, className = "" }) => (
  <motion.div
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-60px" }}
    variants={fadeUp}
    custom={i}
    className={className}
  >
    {children}
  </motion.div>
);

const Home = () => {
  return (
    <main id="top" className="bg-stone-50 text-stone-900 overflow-x-hidden">
      <Seo
        title="A.S.I. Sicilia ETS — Affrontiamo la Sordità Insieme | Messina e Sicilia"
        description="Associazione di volontariato a sostegno delle persone sorde e ipoacusiche e delle loro famiglie in Sicilia: ascolto, orientamento, protesi acustiche, impianti cocleari, diritti e inclusione."
        path="/"
      />
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-[94vh] flex items-end">
        <div className="absolute inset-0">
          <img src="/assets/hero.jpg" alt="Mani che si stringono in segno di sostegno" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-lime-950/95 via-lime-950/55 to-lime-950/40" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 pt-36 w-full">
          <Reveal>
            <Overline>Associazione di Volontariato · Sicilia</Overline>
          </Reveal>
          <motion.h1
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={1}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-white mt-4 max-w-4xl leading-[1.08]"
          >
            Lavoriamo per rendere più semplice, informato e inclusivo il cammino dei portatori di impianto cocleare e per i candidati all'intervento.
          </motion.h1>
          <motion.p
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={2}
            className="mt-6 text-lg md:text-xl text-stone-100/90 max-w-2xl leading-relaxed font-light"
          >
            Supporto per chi ha più bisogno.
          </motion.p>
          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={3}
            className="mt-9 flex flex-wrap gap-3 sm:gap-4"
          >
            <a data-testid="hero-cta-aiuto" href="#aiuto" className="inline-flex items-center gap-2 rounded-full bg-[#E2725B] text-white px-7 py-3.5 font-semibold hover:bg-[#d5604a] transition-colors">
              Chiedi aiuto <ArrowRight size={18} />
            </a>
            <a data-testid="hero-cta-sostienici" href="#sostienici" className="inline-flex items-center gap-2 rounded-full bg-white text-lime-950 px-7 py-3.5 font-semibold hover:bg-stone-100 transition-colors">
              Sostienici
            </a>
            <a data-testid="hero-cta-cosa-facciamo" href="#cosa-facciamo" className="inline-flex items-center gap-2 rounded-full bg-white/10 text-white border border-white/30 backdrop-blur px-7 py-3.5 font-medium hover:bg-white/20 transition-colors">
              Scopri come aiutiamo
            </a>
          </motion.div>
        </div>
      </section>

      {/* FACTS */}
      <section className="bg-emerald-50 border-b border-emerald-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {FACTS.map((f, i) => (
            <Reveal key={i} i={i} className="text-center sm:text-left">
              <p className="font-heading text-3xl sm:text-4xl font-medium text-lime-900">{f.value}</p>
              <p className="text-stone-600 text-sm mt-1">{f.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* COSA FACCIAMO (benefici) */}
      <section id="cosa-facciamo" className="py-24 lg:py-32 bg-rose-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <Overline>Cosa Facciamo</Overline>
            <h2 className="font-heading text-4xl lg:text-5xl font-medium tracking-tight mt-3">
              {WHAT_WE_DO_INTRO.title}
            </h2>
            <p className="text-stone-600 text-lg mt-5 leading-relaxed">
              {WHAT_WE_DO_INTRO.text}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            {BENEFITS.map((b, i) => {
              const Icon = ICONS[b.icon];
              return (
                <Reveal key={b.title} i={i % 3}>
                  <article className="rounded-2xl overflow-hidden border border-stone-200 bg-white h-full flex flex-col hover:-translate-y-1 hover:shadow-md transition-all duration-300">
                    <div className="relative h-44">
                      <img src={b.img} alt={b.title} className="w-full h-full object-cover" />
                      <span className="absolute -bottom-5 left-6 grid place-items-center w-11 h-11 rounded-full bg-lime-900 text-lime-50 shadow-md">
                        {Icon && <Icon size={20} />}
                      </span>
                    </div>
                    <div className="p-7 pt-9 flex-1 flex flex-col">
                      <h3 className="font-heading text-2xl font-medium">{b.title}</h3>
                      <p className="text-stone-600 mt-2 leading-relaxed">{b.text}</p>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* AIUTO */}
      <section id="aiuto" className="py-24 lg:py-32 bg-amber-50 border-y border-amber-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <Overline>Chiedi Aiuto</Overline>
            <h2 className="font-heading text-4xl lg:text-5xl font-medium tracking-tight mt-3">
              Come richiedere il nostro supporto
            </h2>
            <p className="text-stone-600 text-lg mt-5 leading-relaxed">
              È semplice e gratuito. Tre passi per non sentirti più solo di fronte alla sordità.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {HELP_STEPS.map((s, i) => (
              <Reveal key={s.n} i={i}>
                <div className="rounded-2xl bg-white border border-stone-200 p-8 h-full">
                  <span className="font-mono text-5xl font-semibold text-[#E2725B]/30">{s.n}</span>
                  <h3 className="font-heading text-2xl font-medium mt-3">{s.title}</h3>
                  <p className="text-stone-600 mt-2 leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-12 text-center">
            <a data-testid="aiuto-cta-contatti" href="#contatti" className="inline-flex items-center gap-2 rounded-full bg-lime-900 text-lime-50 px-8 py-4 font-semibold hover:bg-lime-800 transition-colors">
              Chiedi aiuto ora <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* TESTIMONIANZE */}
      <section className="py-24 lg:py-32 bg-sky-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <Overline>Storie e Testimonianze</Overline>
            <h2 className="font-heading text-4xl lg:text-5xl font-medium tracking-tight mt-3">
              Le voci di chi abbiamo affiancato
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={i} i={i}>
                <figure className="rounded-2xl bg-white border border-stone-200 p-8 h-full flex flex-col">
                  <Quote className="text-[#E2725B]" size={32} />
                  <blockquote className="font-heading text-xl leading-relaxed text-stone-800 mt-4 flex-1">
                    “{t.quote}”
                  </blockquote>
                  <figcaption className="text-stone-500 text-sm mt-5">— {t.author}</figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CHI SIAMO */}
      <section id="chi-siamo" className="py-24 lg:py-32 bg-violet-50 border-y border-violet-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <Reveal>
            <Overline>Chi Siamo</Overline>
            <h2 className="font-heading text-4xl lg:text-5xl font-medium tracking-tight mt-3">
              Il volontariato è il cuore di tutto
            </h2>
            <p className="text-stone-600 text-lg mt-6 leading-relaxed">
              Siamo un'associazione a carattere volontario senza fini di lucro
              sorta a Messina nel 2016 e composta da pazienti sordi sottoposti
              ad intervento per impianto cocleare presso il Servizio di
              Otorinolaringoiatria dell'A.O.U. “G. Martino”.
            </p>

            <div className="flex flex-wrap gap-3 mt-6">
              <span className="rounded-full bg-white border border-violet-200 px-4 py-1.5 text-sm font-medium text-stone-700">Fondata nel {CONTACT.fondata}</span>
              <span className="rounded-full bg-white border border-violet-200 px-4 py-1.5 text-sm font-medium text-stone-700">{CONTACT.runts}</span>
              <span className="rounded-full bg-white border border-violet-200 px-4 py-1.5 text-sm font-medium text-stone-700">{CONTACT.rep}</span>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 mt-9">
              {[
                { icon: Compass, t: "Missione", d: "Inclusione e diritto alla salute" },
                { icon: Heart, t: "Valori", d: "Volontariato e solidarietà" },
                { icon: HandHeart, t: "Metodo", d: "Ascolto e mutuo aiuto" },
              ].map((b) => (
                <div key={b.t} className="rounded-2xl bg-stone-50 border border-stone-200 p-5">
                  <b.icon className="text-[#E2725B]" size={24} />
                  <p className="font-heading text-lg font-medium mt-3">{b.t}</p>
                  <p className="text-stone-500 text-sm mt-1">{b.d}</p>
                </div>
              ))}
            </div>

            <div className="mt-9">
              <p className="text-sm font-semibold uppercase tracking-wide text-stone-500 flex items-center gap-2">
                <Users size={16} /> Il nostro team
              </p>
              <div className="flex flex-wrap gap-3 mt-3">
                {TEAM.map((m) => (
                  <div key={m.name} className="rounded-full bg-lime-900/5 border border-stone-200 px-5 py-2">
                    <span className="font-medium text-stone-900">{m.name}</span>
                    <span className="text-stone-500 text-sm"> — {m.role}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal i={1} className="grid grid-cols-2 gap-4">
            <img src="/assets/chi-siamo.jpg" alt="Attività dell'associazione" className="rounded-2xl object-cover w-full h-56 sm:h-72 col-span-2" />
            <img src="/assets/mani.jpg" alt="Sostegno e solidarietà" className="rounded-2xl object-cover w-full h-44 sm:h-56" />
            <img src="/assets/prove-udito.jpg" alt="Prove audiometriche" className="rounded-2xl object-cover w-full h-44 sm:h-56" />
          </Reveal>
        </div>
      </section>

      {/* BLOG */}
      <section id="blog" className="py-24 lg:py-32 bg-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <Overline>Blog · Informazione Utile</Overline>
            <h2 className="font-heading text-4xl lg:text-5xl font-medium tracking-tight mt-3">
              Guide e diritti, spiegati semplici
            </h2>
            <p className="text-stone-600 text-lg mt-5 leading-relaxed">
              Articoli pratici su supporto, diritti e agevolazioni per chi vive la sordità in Sicilia.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {BLOG_LIST.map((a, i) => (
              <Reveal key={a.slug} i={i}>
                <Link
                  to={`/blog/${a.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`blog-card-${a.slug}`}
                  className="group block rounded-2xl overflow-hidden border border-stone-200 bg-white h-full hover:-translate-y-1 hover:shadow-md transition-all duration-300"
                >
                  <div className="h-48 overflow-hidden">
                    <img src={a.img} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-7">
                    <div className="flex items-center gap-3 text-xs">
                      <span className="rounded-full bg-[#E2725B]/10 text-[#c2543e] px-3 py-1 font-semibold uppercase tracking-wide">{a.tag}</span>
                      <span className="text-stone-400 font-mono">{a.date}</span>
                    </div>
                    <h3 className="font-heading text-2xl font-medium mt-3">{a.title}</h3>
                    <p className="text-stone-600 mt-2 leading-relaxed">{a.excerpt}</p>
                    <span className="inline-flex items-center gap-2 text-lime-900 font-medium mt-5 text-sm group-hover:gap-3 transition-all">
                      Leggi l'articolo <ArrowRight size={16} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SOSTIENICI */}
      <section id="sostienici" className="py-24 lg:py-32 bg-orange-50 border-y border-orange-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <Overline>Sostienici</Overline>
            <h2 className="font-heading text-4xl lg:text-5xl font-medium tracking-tight mt-3">
              Il tuo aiuto fa la differenza
            </h2>
            <p className="text-stone-600 text-lg mt-5 leading-relaxed">
              Ci sono tanti modi per starci vicino: scegli quello più adatto a te.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {/* 5x1000 */}
            <Reveal>
              <div className="rounded-2xl bg-white border border-stone-200 p-7 h-full shadow-sm">
                <Gift className="text-[#E2725B]" size={28} />
                <h3 className="font-heading text-2xl font-medium mt-4">Dona il 5×1000</h3>
                <p className="text-stone-600 mt-2 text-sm leading-relaxed">Gratis, nella tua dichiarazione dei redditi. Indica il Codice Fiscale:</p>
                <p data-testid="cf-5x1000" className="font-mono text-2xl font-semibold text-lime-900 mt-3 tracking-wide">{CONTACT.cf}</p>
                <img src="/assets/qr-5x1000.jpg" alt="QR Codice Fiscale 5x1000" className="w-44 h-44 rounded-xl border border-stone-200 p-2 bg-white mt-4 object-contain" />
              </div>
            </Reveal>

            {/* Donazione */}
            <Reveal i={1}>
              <div className="rounded-2xl bg-white border border-stone-200 p-7 h-full shadow-sm">
                <CreditCard className="text-[#E2725B]" size={28} />
                <h3 className="font-heading text-2xl font-medium mt-4">Fai una donazione</h3>
                <p className="text-stone-600 mt-2 text-sm leading-relaxed">Con bonifico bancario sull'IBAN:</p>
                <p data-testid="iban" className="font-mono text-sm sm:text-base font-semibold text-lime-900 mt-3 break-all">{CONTACT.iban}</p>
                <img src="/assets/qr-iban.jpg" alt="QR IBAN donazione" className="w-44 h-44 rounded-xl border border-stone-200 p-2 bg-white mt-4 object-contain" />
                <img src="/assets/paypal.png" alt="Dona con PayPal" className="h-10 object-contain mt-3" />
              </div>
            </Reveal>

            {/* Iscriviti */}
            <Reveal i={2}>
              <div className="rounded-2xl bg-white border border-stone-200 p-7 h-full shadow-sm">
                <UserPlus className="text-[#E2725B]" size={28} />
                <h3 className="font-heading text-2xl font-medium mt-4">Diventa socio</h3>
                <p className="text-stone-600 mt-2 text-sm leading-relaxed">Sostienici e partecipa con una quota annua di <span className="text-lime-900 font-semibold">{CONTACT.quota}</span>.</p>
                <a
                  data-testid="socio-candidatura-link"
                  href={CONTACT.candidatura}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-lime-900 text-lime-50 px-5 py-2.5 text-sm font-semibold hover:bg-lime-800 transition-colors mt-4"
                >
                  Presenta la tua candidatura <ArrowRight size={16} />
                </a>
                <p className="text-stone-500 text-xs leading-relaxed mt-3">
                  Al primo accesso clicca su <span className="font-semibold text-stone-700">“Se non hai ancora la password clicca qui per ottenerla”</span>.
                </p>
                <a
                  data-testid="socio-area-riservata-link"
                  href={CONTACT.areaRiservata}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-medium text-stone-600 hover:text-lime-900 transition-colors mt-2"
                >
                  Sei già iscritto? Area riservata →
                </a>
                <div className="mt-4">
                  <img src="/assets/qr-iscrizione.jpg" alt="QR iscrizione" className="w-44 h-44 rounded-xl border border-stone-200 p-2 bg-white object-contain" />
                  <a data-testid="sostienici-cta-volontario" href="#contatti" className="inline-flex items-center gap-2 text-sm font-semibold text-lime-900 hover:gap-3 transition-all mt-3">
                    Voglio fare volontariato <ArrowRight size={16} />
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CONTATTI */}
      <section id="contatti" className="py-24 lg:py-32 bg-lime-50 border-t border-lime-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div>
            <Overline>Contatti</Overline>
            <h2 className="font-heading text-4xl lg:text-5xl font-medium tracking-tight mt-3">
              Siamo qui per te
            </h2>
            <p className="text-stone-600 text-lg mt-5 leading-relaxed">
              Hai bisogno di supporto, informazioni o vuoi diventare volontario?
              Scrivici o chiamaci: ti risponderemo al più presto.
            </p>

            <div className="mt-10 space-y-6">
              <div className="flex items-start gap-4">
                <span className="grid place-items-center w-11 h-11 rounded-full bg-lime-900/10 text-lime-900 shrink-0"><MapPin size={20} /></span>
                <div>
                  <p className="font-medium">Sede</p>
                  <p className="text-stone-600">{CONTACT.sede1}</p>
                  <p className="text-stone-600">{CONTACT.sede2}</p>
                  <p className="text-stone-600">{CONTACT.citta}</p>
                </div>
              </div>
              <a href={`tel:+39${CONTACT.tel.replace(/\s/g, "")}`} target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 group" data-testid="contact-phone-link">
                <span className="grid place-items-center w-11 h-11 rounded-full bg-lime-900/10 text-lime-900 shrink-0"><Phone size={20} /></span>
                <div>
                  <p className="font-medium">Telefono</p>
                  <p className="text-stone-600 group-hover:text-lime-900 transition-colors">{CONTACT.tel}</p>
                </div>
              </a>
              <a href={`mailto:${CONTACT.email}`} target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 group" data-testid="contact-email-link">
                <span className="grid place-items-center w-11 h-11 rounded-full bg-lime-900/10 text-lime-900 shrink-0"><Mail size={20} /></span>
                <div>
                  <p className="font-medium">Email</p>
                  <p className="text-stone-600 group-hover:text-lime-900 transition-colors break-all">{CONTACT.email}</p>
                </div>
              </a>
              <a href={`mailto:${CONTACT.pec}`} target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 group" data-testid="contact-pec-link">
                <span className="grid place-items-center w-11 h-11 rounded-full bg-lime-900/10 text-lime-900 shrink-0"><Mail size={20} /></span>
                <div>
                  <p className="font-medium">PEC</p>
                  <p className="text-stone-600 group-hover:text-lime-900 transition-colors break-all">{CONTACT.pec}</p>
                </div>
              </a>
            </div>
          </div>

          <div className="rounded-2xl bg-stone-50 border border-stone-200 p-8">
            <ContactForm />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-lime-950 text-stone-300 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-10">
            <div className="md:col-span-2">
              <img src="/assets/logo.png" alt="Logo A.S.I. Sicilia ETS" className="h-14 w-auto object-contain" />
              <p className="mt-4 text-stone-400 max-w-sm leading-relaxed">
                {ORG.tagline}. Associazione di volontariato a sostegno delle persone con ipoacusia in Sicilia.
              </p>
              <div className="mt-5 text-sm text-stone-400 space-y-1">
                <p>{CONTACT.runts} · {CONTACT.rep}</p>
                <p>Fondata nel {CONTACT.fondata}</p>
                <p>{CONTACT.sede1}</p>
                <p>{CONTACT.sede2}</p>
                <p>{CONTACT.citta}</p>
              </div>
            </div>
            <div>
              <p className="text-stone-50 font-medium mb-4">Naviga</p>
              <ul className="space-y-2.5 text-sm">
                <li><a href="#cosa-facciamo" className="hover:text-white transition-colors">Cosa Facciamo</a></li>
                <li><a href="#aiuto" className="hover:text-white transition-colors">Chiedi Aiuto</a></li>
                <li><a href="#blog" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#sostienici" className="hover:text-white transition-colors">Sostienici</a></li>
                <li><a href="#contatti" className="hover:text-white transition-colors">Contatti</a></li>
                <li>
                  <a href={CONTACT.areaRiservata} target="_blank" rel="noopener noreferrer" data-testid="footer-area-riservata" className="hover:text-white transition-colors">Area riservata iscritti</a>
                </li>
              </ul>
            </div>
            <div>
              <p className="text-stone-50 font-medium mb-4">Contatti</p>
              <ul className="space-y-2.5 text-sm text-stone-400">
                <li>{CONTACT.referente}</li>
                <li>Tel. {CONTACT.tel}</li>
                <li className="break-all">{CONTACT.email}</li>
                <li className="break-all">PEC: {CONTACT.pec}</li>
              </ul>
            </div>
          </div>

          <div className="mt-14 pt-8 border-t border-lime-900/60 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-stone-400">
            <p>© {new Date().getFullYear()} A.S.I. Sicilia ETS. Tutti i diritti riservati.</p>
            <p className="font-mono">C.F. {CONTACT.cf} · IBAN {CONTACT.iban_plain}</p>
          </div>
        </div>
      </footer>
    </main>
  );
};

export default Home;
